import React, {useEffect, useState} from 'react';
import {
  ActivityIndicator,
  Image,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import {useTranslation} from 'react-i18next';
import AuthHeader from '../../components/AuthHeader';
import PrimaryButton from '../../components/PrimaryButton';
import {getFeedbackById} from '../../services/feedback-service';
import {
  StatusPill,
  formatFeedbackDate,
  ticketLabel,
} from './MyFeedback';
import {colors, card, radii, spacing, typography} from '../../config/theme';

// S3 keys can contain raw spaces; already-encoded URLs are left as they are.
const safeUrl = url => String(url).replace(/ /g, '%20');

const isImage = url => /\.(jpe?g|png|gif|webp)(\?|$)/i.test(String(url));

// The ticket's attachment(s): the backend may send attachmentUrl or a list.
const attachmentsOf = obj => {
  const list =
    obj?.attachmentUrls ||
    obj?.attachments?.map(a => (typeof a === 'string' ? a : a?.url)) ||
    (obj?.attachmentUrl ? [obj.attachmentUrl] : []);
  return (list || []).filter(Boolean);
};

const Attachments = ({urls, label}) =>
  urls.length ? (
    <View style={styles.attachments}>
      {urls.map(url => (
        <Pressable
          key={url}
          onPress={() => Linking.openURL(safeUrl(url))}
          accessibilityRole="link"
          accessibilityLabel={label}>
          {isImage(url) ? (
            <Image source={{uri: safeUrl(url)}} style={styles.thumb} />
          ) : (
            <Text style={styles.link}>{label}</Text>
          )}
        </Pressable>
      ))}
    </View>
  ) : null;

// One ticket (GET feedback/:id) with the replies Pompi emailed.
const FeedbackDetail = ({navigation, route}) => {
  const {t} = useTranslation();
  const {id, item: preview} = route.params || {};
  const [ticket, setTicket] = useState(preview || null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const load = async () => {
    setLoading(true);
    const result = await getFeedbackById(id);
    if (result.success && result.data) {
      // Some APIs wrap the ticket: {feedback: {...}, replies: [...]}
      const data = result.data.feedback
        ? {...result.data.feedback, replies: result.data.replies}
        : result.data;
      setTicket(data);
      setLoadError(null);
    } else {
      setLoadError(result.error);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const replies = Array.isArray(ticket?.replies) ? ticket.replies : [];

  return (
    <View style={styles.screen}>
      <AuthHeader
        title={ticket ? ticketLabel(ticket) : t('root.Feedback.ticket')}
        onBack={() => navigation.goBack()}
      />
      {!ticket && loading ? (
        <ActivityIndicator style={styles.loader} color={colors.primary} />
      ) : !ticket ? (
        <View style={styles.content}>
          <Text style={styles.muted}>
            {loadError || t('root.Feedback.loadFailed')}
          </Text>
          <PrimaryButton title={t('root.Feedback.retry')} onPress={load} />
        </View>
      ) : (
        <ScrollView
          contentContainerStyle={styles.content}
          showsVerticalScrollIndicator={false}>
          <View style={styles.card}>
            <View style={styles.row}>
              <Text style={styles.date}>
                {formatFeedbackDate(ticket.createdAt)}
              </Text>
              <StatusPill status={ticket.status} />
            </View>
            <Text style={styles.subject}>{ticket.subject}</Text>
            <Text style={styles.label}>{t('root.Feedback.yourMessage')}</Text>
            <Text style={styles.body}>{ticket.message}</Text>
            <Attachments
              urls={attachmentsOf(ticket)}
              label={t('root.Feedback.attachment')}
            />
          </View>

          <Text style={styles.section}>{t('root.Feedback.replies')}</Text>
          {loading ? (
            <ActivityIndicator color={colors.primary} />
          ) : replies.length ? (
            replies.map((reply, index) => (
              <View key={reply.id ?? index} style={[styles.card, styles.reply]}>
                <View style={styles.row}>
                  <Text style={styles.replySubject} numberOfLines={2}>
                    {reply.subject || 'Pompi'}
                  </Text>
                  <Text style={styles.date}>
                    {formatFeedbackDate(reply.sentAt || reply.createdAt)}
                  </Text>
                </View>
                <Text style={styles.body}>{reply.body || reply.message}</Text>
                <Attachments
                  urls={attachmentsOf(reply)}
                  label={t('root.Feedback.attachment')}
                />
              </View>
            ))
          ) : (
            <Text style={styles.muted}>{t('root.Feedback.noReplies')}</Text>
          )}
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {flex: 1, backgroundColor: colors.background},
  loader: {marginTop: spacing.xxl},
  content: {
    paddingHorizontal: spacing.margin,
    paddingTop: spacing.md,
    paddingBottom: spacing.xxl,
    gap: spacing.md,
  },
  card: {...card, padding: spacing.md, gap: spacing.sm},
  reply: {borderColor: colors.mintLine},
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  subject: {...typography.headlineSm, color: colors.text},
  replySubject: {
    ...typography.labelLg,
    letterSpacing: 0,
    color: colors.text,
    flex: 1,
  },
  label: {...typography.labelMd, color: colors.textMuted, marginTop: spacing.xs},
  body: {...typography.bodyMd, color: colors.textEmphasis},
  date: {...typography.bodySm, color: colors.textSubtle},
  section: {...typography.labelLg, color: colors.textStrong, marginTop: spacing.sm},
  muted: {...typography.bodyMd, color: colors.textMuted},
  attachments: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm},
  thumb: {
    width: 72,
    height: 72,
    borderRadius: radii.sm,
    backgroundColor: colors.surfaceMuted,
  },
  link: {...typography.labelMd, color: colors.brandLink},
});

export default FeedbackDetail;
